import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Play, Lock, Settings2 } from "lucide-react";
import { api, getToken } from "../api";
import { useAuth } from "../auth";
import { mediaUrl, fmtDur, localApi } from "../config";
import Player from "../components/Player";
import GatedPlayer, { LockedOverlay } from "../components/GatedPlayer";
import { PaywallModal, SupportButton } from "../components/Payments";
import { SeriesCard, Avatar, Spinner, Empty, SectionTitle } from "../components/cards";
import { orderFeed } from "../subscriptions";

const gbp = (n) => `£${(Number(n) || 0).toFixed(2)}`;

export const SeriesList = () => {
  const { user } = useAuth();
  const [series, setSeries] = useState(null);
  useEffect(() => {
    api.get("/series?limit=60").then((r) => setSeries(Array.isArray(r) ? r : r.series || [])).catch(() => setSeries([]));
  }, []);

  const feed = useMemo(() => (series ? orderFeed(series, user) : null), [series, user]);
  if (series === null || feed === null) return <Spinner />;
  if (series.length === 0) return <Empty>No series yet.</Empty>;
  const list = [...(feed.fromSubs || []), ...(feed.rest || [])];

  return (
    <div className="mx-auto max-w-7xl" data-testid="series-page">
      <h1 className="mb-6 font-heading text-2xl font-bold tracking-tight text-white">Series</h1>
      {feed.personalized && feed.fromSubs.length > 0 && (
        <section className="mb-8" data-testid="series-from-subscriptions">
          <SectionTitle>From your subscriptions</SectionTitle>
          <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 xl:grid-cols-5">
            {feed.fromSubs.map((s) => (
              <SeriesCard key={s.id} series={s} />
            ))}
          </div>
        </section>
      )}
      {feed.personalized && <SectionTitle>More series</SectionTitle>}
      <div className="grid grid-cols-2 gap-5 sm:grid-cols-3 xl:grid-cols-5" data-testid="series-grid">
        {(feed.personalized ? feed.rest : list).map((s) => (
          <SeriesCard key={s.id} series={s} />
        ))}
      </div>
      <p className="mt-6 text-xs text-slate-600">All series episodes are free to watch on the web.</p>
    </div>
  );
};

export const SeriesDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [current, setCurrent] = useState(null);
  const [access, setAccess] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false);
  const [locked, setLocked] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [sales, setSales] = useState(null);

  useEffect(() => {
    setData(null);
    setCurrent(null);
    setAccess(null);
    setLocked(false);
    setShowPaywall(false);
    api.get(`/series/${id}`).then((r) => {
      const series = r.series ?? r;
      const episodes = r.episodes ?? series.episodes ?? [];
      setData({ ...series, episodes });
      if (episodes[0]) setCurrent(episodes[0]);
    }).catch(() => setData({ episodes: [] }));
    localApi("GET", `/series/${id}/access`, undefined, getToken()).then(setAccess).catch(() => setAccess({ paid: false, owned: true }));
  }, [id]);

  useEffect(() => {
    if (access?.is_creator) {
      localApi("GET", `/series/${id}/sales`, undefined, getToken()).then(setSales).catch(() => setSales(null));
    }
  }, [id, access]);

  if (data === null || access === null) return <Spinner />;

  const episodes = [...(data.episodes || [])].sort(
    (a, b) => (a.seasonNumber ?? 0) - (b.seasonNumber ?? 0) || (a.episodeNumber ?? 0) - (b.episodeNumber ?? 0)
  );

  const isCreator = access.is_creator;
  const isLocked = access.paid && !access.owned; // viewer who hasn't bought
  const previewSec = access.preview_sec ?? 600;

  const pickEpisode = (ep) => {
    setCurrent(ep);
    setLocked(false);
  };

  return (
    <div className="mx-auto max-w-5xl" data-testid="series-detail-page">
      <div className="mb-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="font-heading text-2xl font-bold tracking-tight text-white" data-testid="series-title">{data.title}</h1>
          {isCreator && (
            <button onClick={() => setShowConfig(true)} data-testid="series-pricing-button" className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 font-heading text-xs font-bold text-slate-200 transition-colors hover:border-crimson/50">
              <Settings2 size={14} /> {access.paid ? "Pricing" : "Set as Paid"}
            </button>
          )}
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-3">
          {data.creator && (
            <Link to={`/app/creator/${data.creator.id || data.creatorId}`} className="flex items-center gap-2 text-sm text-slate-400 hover:text-white">
              <Avatar user={data.creator} size={24} />
              {data.creator.displayName || data.creator.username}
            </Link>
          )}
          {data.creator && <SupportButton target={{ id, title: data.title }} creator={data.creator} />}
        </div>
        {access.paid && (
          <p className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-crimson/40 bg-crimson/10 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-crimson" data-testid="series-paid-badge">
            <Lock size={11} /> Paid series · {gbp(access.price)}
          </p>
        )}
        {isCreator && access.paid && sales && (
          <p className="mt-3 text-sm text-slate-400" data-testid="series-sales">
            <span className="font-bold text-white">{sales.buyers}</span> {sales.buyers === 1 ? "person has" : "people have"} bought this series · earned <span className="font-bold text-emerald-400">{gbp(sales.earned)}</span>
          </p>
        )}
        {data.description && <p className="mt-3 text-sm leading-relaxed text-slate-400">{data.description}</p>}
      </div>

      {current && (
        <div className="mb-6">
          <div className="relative">
            {isLocked ? (
              <>
                <GatedPlayer
                  video={current}
                  previewSec={previewSec}
                  locked
                  className="aspect-video w-full rounded-2xl"
                  onLocked={() => setLocked(true)}
                />
                {locked && <LockedOverlay title={data.title} />}
              </>
            ) : (
              <Player video={current} className="aspect-video w-full rounded-2xl" />
            )}
          </div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            <p className="font-heading text-sm font-semibold text-white" data-testid="now-playing-episode">
              {current.seasonNumber != null ? `S${current.seasonNumber} · E${current.episodeNumber} — ` : ""}{current.title}
            </p>
            {isLocked && (
              <button onClick={() => setShowPaywall(true)} data-testid="series-unlock-button" className="inline-flex items-center gap-2 rounded-full bg-crimson px-5 py-2.5 font-heading text-xs font-bold text-white transition-colors hover:bg-crimson-hover">
                <Lock size={13} /> Pay {gbp(access.price)} to continue
              </button>
            )}
          </div>
          {isLocked && !locked && (
            <p className="mt-2 text-xs text-slate-500" data-testid="preview-note">Free preview: first {Math.round(previewSec / 60)} minute{Math.round(previewSec / 60) === 1 ? "" : "s"} of this series.</p>
          )}
        </div>
      )}

      <h2 className="mb-3 font-heading text-base font-bold text-white">Episodes</h2>
      {episodes.length === 0 ? (
        <Empty>No episodes yet.</Empty>
      ) : (
        <div className="space-y-2">
          {episodes.map((ep) => (
            <button
              key={ep.id}
              onClick={() => pickEpisode(ep)}
              data-testid={`episode-${ep.id}`}
              className={`flex w-full items-center gap-4 rounded-xl border p-3 text-left transition-colors ${current?.id === ep.id ? "border-crimson/50 bg-crimson/10" : "border-white/10 bg-white/[0.02] hover:border-white/25"}`}
            >
              <div className="relative h-16 w-28 shrink-0 overflow-hidden rounded-lg bg-surface">
                {mediaUrl(ep.thumbnail) && <img src={mediaUrl(ep.thumbnail)} alt="" className="h-full w-full object-cover" />}
                <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                  {isLocked ? <Lock size={16} className="text-crimson" /> : <Play size={16} className="text-white" fill="white" />}
                </div>
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate font-heading text-sm font-semibold text-white">
                  {ep.seasonNumber != null ? `S${ep.seasonNumber} · E${ep.episodeNumber} — ` : ""}{ep.title}
                </p>
                {ep.durationSec > 0 && <p className="font-mono text-[10px] text-slate-500">{fmtDur(ep.durationSec)}</p>}
              </div>
            </button>
          ))}
        </div>
      )}

      {showPaywall && (
        <PaywallModal
          series={data}
          access={access}
          onClose={() => setShowPaywall(false)}
          onOwned={() => {
            setShowPaywall(false);
            setAccess((a) => ({ ...a, owned: true }));
            setLocked(false);
          }}
        />
      )}

      {showConfig && (
        <PricingModal
          series={data}
          access={access}
          onClose={() => setShowConfig(false)}
          onSaved={(cfg) => {
            setShowConfig(false);
            setAccess((a) => ({ ...a, paid: true, price: cfg.price, preview_sec: cfg.preview_sec, show_ads: cfg.show_ads, owned: true, is_creator: true }));
          }}
          onRemoved={() => {
            setShowConfig(false);
            setAccess({ paid: false, owned: true, is_creator: true, show_ads: true });
          }}
        />
      )}
    </div>
  );
};

// Creator sets Paid price + preview length + ads choice for their own series.
const PricingModal = ({ series, access, onClose, onSaved, onRemoved }) => {
  const [price, setPrice] = useState(access.paid ? String(access.price) : "");
  const [preview, setPreview] = useState(access.paid ? String(Math.round((access.preview_sec ?? 600) / 60)) : "10");
  const [showAds, setShowAds] = useState(access.show_ads !== false);
  const [confirmNoAds, setConfirmNoAds] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    const p = parseFloat(price);
    if (!p || p <= 0) { setErr("Enter a price."); return; }
    if (!showAds && !confirmNoAds) { setConfirmNoAds(true); return; }
    setBusy(true);
    setErr("");
    try {
      const r = await localApi("POST", `/series/${series.id}/paid`, { price: p, preview_minutes: parseFloat(preview) || 0, show_ads: showAds }, getToken());
      onSaved(r);
    } catch (e) {
      setErr(e.message || "Could not save.");
    }
    setBusy(false);
  };

  const remove = async () => {
    setBusy(true);
    try {
      await localApi("DELETE", `/series/${series.id}/paid`, undefined, getToken());
      onRemoved();
    } catch (e) {
      setErr(e.message || "Could not remove pricing.");
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" onClick={onClose} data-testid="pricing-modal">
      <div className="w-full max-w-md rounded-3xl border border-white/10 bg-[#0c0c12] p-6" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-heading text-lg font-bold text-white">Paid series</h3>
        <p className="mt-1 text-sm text-slate-400">One price unlocks the whole series — every season and episode.</p>

        {confirmNoAds ? (
          <div className="mt-5">
            <p className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm leading-relaxed text-amber-300" data-testid="noads-warning">
              The app will take 40% of the revenue from this series and you keep 60%, and views of this series will not earn revenue per view. With ads, you keep 100% and still earn revenue per view.
            </p>
            <div className="mt-5 flex justify-end gap-3">
              <button onClick={() => { setConfirmNoAds(false); setShowAds(true); }} className="rounded-full border border-white/15 px-5 py-2.5 font-heading text-xs font-bold text-slate-300 hover:border-white/40" data-testid="noads-back">Keep ads</button>
              <button onClick={submit} disabled={busy} className="rounded-full bg-crimson px-5 py-2.5 font-heading text-xs font-bold text-white hover:bg-crimson-hover disabled:opacity-50" data-testid="noads-confirm">{busy ? "Saving…" : "Confirm no ads"}</button>
            </div>
          </div>
        ) : (
          <>
            <div className="mt-5 space-y-4">
              <div>
                <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-slate-500">Price (GBP)</label>
                <div className="relative">
                  <span className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-500">£</span>
                  <input type="number" min="0.5" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="e.g. 4.99" data-testid="pricing-price" className="w-full rounded-xl border border-white/10 bg-white/[0.04] py-3 pl-8 pr-4 text-sm text-white outline-none focus:border-crimson/60" />
                </div>
              </div>
              <div>
                <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-slate-500">Free preview (minutes, max 10)</label>
                <input type="number" min="0" max="10" step="0.5" value={preview} onChange={(e) => setPreview(e.target.value)} data-testid="pricing-preview" className="w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white outline-none focus:border-crimson/60" />
              </div>
              <div>
                <label className="mb-1.5 block font-mono text-[10px] uppercase tracking-widest text-slate-500">Ads</label>
                <div className="flex gap-2">
                  <button onClick={() => setShowAds(true)} data-testid="pricing-ads-on" className={`flex-1 rounded-xl border px-4 py-2.5 font-heading text-xs font-bold transition-colors ${showAds ? "border-crimson bg-crimson/15 text-white" : "border-white/15 text-slate-400 hover:border-white/30"}`}>Show ads</button>
                  <button onClick={() => setShowAds(false)} data-testid="pricing-ads-off" className={`flex-1 rounded-xl border px-4 py-2.5 font-heading text-xs font-bold transition-colors ${!showAds ? "border-crimson bg-crimson/15 text-white" : "border-white/15 text-slate-400 hover:border-white/30"}`}>No ads</button>
                </div>
                <p className="mt-2 text-xs text-slate-500">{showAds ? "You keep 100% of sales and still earn revenue per view." : "The app takes 40% of sales, you keep 60%, and this series earns no revenue per view."}</p>
              </div>
            </div>
            {err && <p className="mt-3 rounded-xl border border-crimson/30 bg-crimson/10 px-4 py-3 text-sm text-crimson" data-testid="pricing-error">{err}</p>}
            <div className="mt-5 flex items-center justify-between gap-3">
              {access.paid ? (
                <button onClick={remove} disabled={busy} className="font-heading text-xs font-bold text-crimson hover:underline disabled:opacity-50" data-testid="pricing-remove">Make free</button>
              ) : <span />}
              <div className="flex gap-3">
                <button onClick={onClose} className="rounded-full border border-white/15 px-5 py-2.5 font-heading text-xs font-bold text-slate-300 hover:border-white/40" data-testid="pricing-cancel">Cancel</button>
                <button onClick={submit} disabled={busy} className="rounded-full bg-crimson px-5 py-2.5 font-heading text-xs font-bold text-white hover:bg-crimson-hover disabled:opacity-50" data-testid="pricing-save">{busy ? "Saving…" : "Save"}</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
